/* Application shell — one fixed left rail, for both roles.
 *
 * Employees used to get a "solo" layout with no rail and a top bar carrying the brand and the
 * account menu, while admins got the rail AND that same bar. So the bar duplicated the rail for
 * one role and was the only chrome for the other, and the account control moved depending on
 * who you were. Now the rail is the single home for navigation and identity; the bar survives
 * only as the admin's burger below 900px, where the rail goes off-canvas.
 *
 * Auth still gates everything: until /api/me resolves, the SSO landing renders. Local
 * development signs in through the prototype-styled login page (a directory picker with
 * demo-password theatre); a deployed build goes straight to Entra pass-through.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { ApiError, getDemoIdentity, hil, identity, notifications, setDemoIdentity } from './api/client'
import type { Me } from './api/types'
import ChatScreen from './screens/ChatScreen'
import DocumentRedirect from './screens/DocumentRedirect'
import Login from './screens/Login'
import { SsoLanding } from './screens/SsoLanding'
import AnalyticsScreen from './screens/admin/AnalyticsScreen'
import AuditScreen from './screens/admin/AuditScreen'
import DocumentsScreen from './screens/admin/DocumentsScreen'
import IngestScreen from './screens/admin/IngestScreen'
import NotificationsScreen from './screens/admin/NotificationsScreen'
import ModelScreen from './screens/admin/ModelScreen'
import UsersScreen from './screens/admin/UsersScreen'
import { EmptyState, initialsOf, useModal, useToast } from './ui/chrome'
import { Icon, type IconName } from './ui/icons'
import { buildPDF, currentConversationExport, slug } from './ui/pdf'

type AuthState =
  | { phase: 'authenticating' }
  | { phase: 'ready'; me: Me }
  | { phase: 'denied'; error: ApiError }
  | { phase: 'error'; error: ApiError }

/* Local development runs without an Entra tenant (prerequisites P1-P3), so identity comes from
 * the demo directory picker instead of a token. `import.meta.env.DEV` is false in any
 * production build; a demo-server build re-enables the picker only when it was built with
 * VITE_DEMO_SIGN_IN=true — an explicit, at-build-time decision that a real production
 * pipeline never sets. The server still refuses the bypass outside APP_ENV=local. */
const DEMO_SIGN_IN = import.meta.env.DEV || import.meta.env.VITE_DEMO_SIGN_IN === 'true'

interface NavItem {
  to: string
  label: string
  ic: IconName
  badge?: 'hil' | 'drafts'
}

/* Two roles: employee and admin. An employee gets chat alone; an admin gets everything.
 * Labels are one short word each — the Option B rail is 76px wide and sets them at 9.5px
 * under the icon, so "Ingestion & HIL Review" becomes "Ingest". */
function navFor(me: Me): NavItem[] {
  const items: NavItem[] = [{ to: '/chat', label: 'Ask', ic: 'chat' }]
  if (me.is_admin) {
    items.push(
      { to: '/admin/analytics', label: 'Insights', ic: 'chart' },
      { to: '/admin/documents', label: 'Library', ic: 'book' },
      { to: '/admin/ingest', label: 'Ingest', ic: 'cloud', badge: 'hil' },
      { to: '/admin/notifications', label: 'Alerts', ic: 'mail', badge: 'drafts' },
      { to: '/admin/users', label: 'Users', ic: 'users' },
      { to: '/admin/audit', label: 'Audit', ic: 'list' },
      // Which model answers. Last in the rail because it is set rarely and read often —
      // it belongs with the settings-shaped surfaces, not between the daily ones.
      { to: '/admin/model', label: 'Model', ic: 'brain' },
    )
  }
  return items
}

function Shell({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const location = useLocation()
  const toast = useToast()
  const { openModal, closeModal } = useModal()

  const isAdmin = me.is_admin
  const nav = navFor(me)
  const onChat = location.pathname.startsWith('/chat')

  const [navOpen, setNavOpen] = useState(false)
  const [badges, setBadges] = useState<{ hil: number; drafts: number }>({ hil: 0, drafts: 0 })
  const contentRef = useRef<HTMLElement>(null)
  const sessionStart = useRef(Date.now())

  const refreshBadges = useCallback(() => {
    if (!isAdmin) return
    hil
      .listReviews({ status: 'queued' })
      .then((queue) => setBadges((b) => ({ ...b, hil: queue.pending_count })))
      .catch(() => {})
    // Only the count is needed now: it drives the Notifications badge on the rail. The drawer
    // that used to render these items was removed with the top bar — the Notifications screen
    // one click away in the rail is the same list, in full.
    notifications
      .list('pending_approval')
      .then((items) => setBadges((b) => ({ ...b, drafts: items.length })))
      .catch(() => setBadges((b) => ({ ...b, drafts: 0 })))
  }, [isAdmin])

  useEffect(() => {
    refreshBadges()
  }, [refreshBadges, location.pathname])

  useEffect(() => {
    setNavOpen(false)
    contentRef.current?.scrollTo(0, 0)
  }, [location.pathname])

  const roleLabel = me.is_admin ? 'Admin' : 'Employee'

  function openUserMenu() {
    const exportable = onChat ? currentConversationExport() : null
    openModal({
      title: me.display_name,
      sub: `${roleLabel} · ${me.department ?? 'KBL'}`,
      size: 'narrow',
      body: (
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-b">
            <div className="stat-line">
              <span className="sl-k">Email</span>
              <span className="sl-v">{me.upn}</span>
            </div>
            <div className="stat-line">
              <span className="sl-k">Department</span>
              <span className="sl-v">{me.department ?? '—'}</span>
            </div>
            <div className="stat-line">
              <span className="sl-k">Signed in via</span>
              <span className="sl-v">Azure AD / Entra ID</span>
            </div>
            <div className="stat-line">
              <span className="sl-k">Session age</span>
              <span className="sl-v">{Math.floor((Date.now() - sessionStart.current) / 60000)} min</span>
            </div>
            <div className="stat-line">
              <span className="sl-k">Entra ID groups</span>
              <span className="sl-v">
                {me.entra_group_ids.length > 0
                  ? me.entra_group_ids.map((g) => (
                      <span key={g} className="tag grp">
                        {g}
                      </span>
                    ))
                  : '—'}
              </span>
            </div>
          </div>
        </div>
      ),
      footer: (
        <>
          <button type="button" className="btn btn-soft" onClick={closeModal}>
            Close
          </button>
          {DEMO_SIGN_IN && (
            <button
              type="button"
              className="btn btn-soft"
              onClick={() => {
                closeModal()
                onSignOut()
              }}
            >
              <Icon name="refresh" size={16} /> Switch identity
            </button>
          )}
          {exportable && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                const data = currentConversationExport()
                if (!data || data.blocks.length === 0) {
                  toast('warn', 'Nothing to export', 'Ask a question first.')
                  return
                }
                const fn = `kbl-conversation-${slug(data.title)}.pdf`
                buildPDF(
                  `Conversation - ${data.title}`,
                  [`Exported by ${me.display_name}`, `Exported ${new Date().toLocaleString('en-IN')}`],
                  data.blocks,
                  fn,
                )
                toast('ok', 'Conversation exported', fn)
                closeModal()
              }}
            >
              <Icon name="dl" size={16} /> Export conversation
            </button>
          )}
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              closeModal()
              onSignOut()
            }}
          >
            <Icon name="logout" size={16} /> Sign out
          </button>
        </>
      ),
    })
  }

  return (
    <div id="app-root" className="on">
      <div className="app">
        {/* Everyone gets the rail now, employees included. They used to get a "solo" layout
            whose only chrome was a top bar carrying the brand and an account menu — which meant
            the one identity control in the product sat in a different place depending on who
            you were. One rail, one place for the account, for both roles. */}
          {/* Option B icon rail: 76px, logo tile on top, icon-over-label items, the
              account avatar pinned to the bottom. Conversations live inside ChatScreen's
              own collapsible panel for both roles now — the rail carries navigation only. */}
          <aside className={`nav${navOpen ? ' open' : ''}`} id="navSide">
            {/* The wordmark itself, not the packaged logo card: kbl-logo.png is a white-
                bordered tile around a dark panel, and cropping that to a 40px square left the
                script tiny inside its own packaging. */}
            <div className="nav-logo" title="Kirloskar Brothers Limited · Internal AI Assistant">
              <img src="/kbl-logo-mark-navy.png" alt="Kirloskar Brothers Limited" />
            </div>
            <nav className="nav-list">
              {nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  title={item.label}
                  className={({ isActive }) => `nav-i${isActive ? ' on' : ''}`}
                >
                  <Icon name={item.ic} />
                  <span>{item.label}</span>
                  {item.badge && badges[item.badge] > 0 && (
                    <span className="nav-b">{badges[item.badge]}</span>
                  )}
                </NavLink>
              ))}
            </nav>
            <button
              type="button"
              className="nav-user"
              onClick={openUserMenu}
              title={`${me.display_name} · ${roleLabel}`}
            >
              <span className="avatar nav-avatar">{initialsOf(me.display_name)}</span>
            </button>
          </aside>
          <div
            className={`nav-scrim${navOpen ? ' on' : ''}`}
            id="navScrim"
            onClick={() => setNavOpen(false)}
          />

        <div className="shell">
          {/* No top bar on a wide screen. Beside the rail it carried nothing the rail did not
              already have — the same profile, and a bell whose badge was literally
              `badges.drafts`, the number already shown on the Notifications nav item next to
              it — so it was a full-width empty strip.
              What survives is the burger, and only below 900px (see .topbar-min), where the
              rail slides off-canvas and this is the only way back to it. */}
          <header className="topbar topbar-min">
            <button
              type="button"
              className="tb-burger"
              title="Menu"
              onClick={() => setNavOpen((open) => !open)}
            >
              <Icon name="list" />
            </button>
          </header>

          <main className={`content${onChat ? ' flush' : ''}`} id="content" ref={contentRef}>
            <Routes>
              <Route path="/" element={<Navigate to="/chat" replace />} />
              <Route path="/chat" element={<ChatScreen me={me} />} />
              {/* Where a notification email's "View document" button lands. Outside the
                  admin block: any employee who can read the document may open it. */}
              <Route path="/document/:id" element={<DocumentRedirect />} />

              {isAdmin && (
                <>
                  <Route path="/admin" element={<Navigate to="/admin/analytics" replace />} />
                  <Route path="/admin/ingest" element={<IngestScreen onChanged={refreshBadges} />} />
                  <Route path="/admin/analytics" element={<AnalyticsScreen me={me} />} />
                  <Route path="/admin/documents" element={<DocumentsScreen />} />
                  <Route
                    path="/admin/notifications"
                    element={<NotificationsScreen onChanged={refreshBadges} />}
                  />
                  <Route path="/admin/users" element={<UsersScreen currentUpn={me.upn} />} />
                  <Route path="/admin/audit" element={<AuditScreen />} />
                  <Route path="/admin/model" element={<ModelScreen />} />
                </>
              )}

              <Route
                path="*"
                element={
                  <EmptyState
                    icon="alert"
                    title="Page not found"
                    msg="That page does not exist in the assistant."
                  />
                }
              />
            </Routes>
          </main>
        </div>
      </div>

    </div>
  )
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ phase: 'authenticating' })
  // Who the demo picker chose. Null means "show the picker" — which is also what a fresh
  // browser and a signed-out one look like, so there is one path rather than three.
  const [chosen, setChosen] = useState<string | null>(() => getDemoIdentity()?.upn ?? null)

  const signIn = useCallback(() => {
    if (DEMO_SIGN_IN && !getDemoIdentity()) {
      return
    }
    setAuth({ phase: 'authenticating' })
    identity
      .me()
      .then((me) => setAuth({ phase: 'ready', me }))
      .catch((cause: unknown) => {
        const error =
          cause instanceof ApiError ? cause : new ApiError('internal_error', 'Sign-in failed.', 0)
        setAuth({
          phase: error.isAccessDenied || error.isUnauthenticated ? 'denied' : 'error',
          error,
        })
      })
  }, [])

  useEffect(signIn, [signIn, chosen])

  const signOut = useCallback(() => {
    setDemoIdentity(null)
    setChosen(null)
    setAuth({ phase: 'authenticating' })
  }, [])

  if (DEMO_SIGN_IN && !chosen) {
    return <Login onSignedIn={() => setChosen(getDemoIdentity()?.upn ?? null)} />
  }

  if (auth.phase !== 'ready') {
    return (
      <SsoLanding
        identityHint={getDemoIdentity()?.upn ?? null}
        status={auth.phase}
        error={auth.phase === 'authenticating' ? null : auth.error}
        onRetry={signIn}
        onSignIn={DEMO_SIGN_IN ? signOut : signIn}
      />
    )
  }

  return <Shell me={auth.me} onSignOut={signOut} />
}
